import { defineStore } from 'pinia'
import { without } from 'lodash-es'
import { useGeneratedConfigStore, useGeneratedEmployeeConfigStore } from '@/stores'
import { apiConfigsGetCollection, apiEmployeeConfigsGetCollection, ConfigJsonldDefaultRead, EmployeeConfigDefaultWrite, EmployeeConfigJsonldDefaultRead } from '@/api'
import { useMercure } from '@/composables/useMercure.ts'
import { type Logger, useLogger } from '@/composables/useLogger.ts'
import type { RemovableRef } from '@vueuse/core'
import { useTitrop } from '@/composables/useTitrop.ts'
import { useRouter } from 'vue-router'
import { ref, type Ref, toRaw } from 'vue'
import { useTitropEvents } from '@/composables/useTitropEvents.ts'

type EmployeeConfigEx = Omit<EmployeeConfigJsonldDefaultRead, 'value'> &
    Required<Pick<EmployeeConfigJsonldDefaultRead, 'key'>> & {
  value: boolean | string | null | number
}
// type EmployeeConfigDefaultEx = Omit<EmployeeConfigDefaultJsonldDefaultRead, 'value'> & Required<Pick<EmployeeConfigDefaultJsonldDefaultRead, 'key'>> & { value: boolean | string | null | number }
type ApplicationConfigEx = Omit<ConfigJsonldDefaultRead, 'value'> &
    Required<Pick<ConfigJsonldDefaultRead, 'key'>> & {
  value: boolean | string | null | number
}

type PathedRef<T> = RemovableRef<T> & {
  isReal: Readonly<Ref<boolean>>
}
// type NotRef<T> = T extends Ref<never> ? never : T
useMercure().subscribe<EmployeeConfigEx>('EmployeeConfig', (data: EmployeeConfigEx) => {
  const store = useConfigStore()
  if (data.employee != useTitrop().value.user.currentIri) return

  store.$logger.debug(`Mercure update: ${data.key} = ${data.value}`)

  if (store._pendingRefreshKeys.has(data.key)) {
    store._pendingRefreshKeys.delete(data.key)
    store.$logger.debug(`Mercure: skip key ${data.key} (was locally updated)`)
    return
  }

  store.$patch((state) => {
    if (data.value === null) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete state.$user[data.key]
    } else {
      state.$user[data.key] = data.value
    }
  })
})
// Mercure.subscribe<EmployeeConfigDefaultEx>('EmployeeConfigDefault', (data: EmployeeConfigDefaultEx) => {
//     const store = useConfigStore()
//     if (store.$user[data.key] === undefined) store.set(data.key, data.value)
//     else store.$logger.info(`Ignored change to default user setting ${data.key} due to already existing`)
// })
useMercure().subscribe<ApplicationConfigEx>('Config', (data: ApplicationConfigEx) => {
  const store = useConfigStore()
  store.$patch((state) => {
    if (data.value === null) {
      Reflect.deleteProperty(state.$app, data.key)
    } else {
      state.$app[data.key] = data.value
    }
  })
})
// const serializer = {
//     read: (v: never) => {
//         return v ? JSON.parse(v) : null
//     },
//     write: (v: never) => {
//         if (['string', 'boolean', 'number', 'bigint', 'undefined'].includes(typeof v)) return v
//         return JSON.stringify(v)
//     }
// }

// const ConfigAdaptor = new (class implements StorageLike {
//     removeItem(key: string) {
//         useConfigStore().set(key, null)
//     }
//
//     getItem(key: string) {
//         const store = useConfigStore().$user
//         if (!store[key]) debugger
//         return store[key] ?? null
//     }
//
//     setItem(key: string, value: string) {
//         useConfigStore().set(key, value)
//     }
// })()
export const useConfigStore = defineStore('config-store', {
  state: (): {
    $doUpdateOnChange: boolean
    $user: Record<string, unknown>
    $app: Record<string, unknown>
    $refresher: NodeJS.Timeout | undefined
    $logger: Logger
    ready: boolean
    _pendingRefreshKeys: Set<string>
    _refCache: Map<string, Ref<unknown>>
  } => ({
    $doUpdateOnChange: false,
    $user: {},
    $app: {},
    $refresher: undefined,
    $logger: useLogger('Config-Store'),
    ready: false,

    _pendingRefreshKeys: new Set<string>(),
    _refCache: new Map<string, Ref<unknown>>()
  }),
  persist: false,
  actions: {
    // app_get<T>(key: string, defaultValue?: T | undefined | null): Promise<T> {
    //     const kv = this.$app[key]
    //     if (!kv || kv.value === undefined || kv.value === null) return defaultValue
    //     return <T>kv.value
    // },
    clear(): void {
      this.$patch({
        $user: {},
        $app: {},
        ready: false
      })
      this._refCache.clear()
    },

    async $refresh(): Promise<void> {
      this.$doUpdateOnChange = false
      //emp
      const s = useGeneratedEmployeeConfigStore()
      s.$reset()

      let empConfig: EmployeeConfigEx[] = []
      let counter = 0
      do {
        if (counter > 0) console.warn(`Failed to get employee configs - retry #${counter}`)
        empConfig = (await apiEmployeeConfigsGetCollection<false>().then((d) => d.data?.['hydra:member'])) as EmployeeConfigEx[]
        counter++
      } while (!empConfig && counter < 5)
      if (!empConfig || empConfig.length == 0) {
        console.warn('Received emp config was empty!')
      }

      const empExcess = without(Object.keys(this.$user), ...(empConfig ?? []).map((e) => e.key))

      for (const key of empExcess)
        this.$patch((state) => {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete state.$user[key]
        })
      for (const conf of empConfig) {
        if (this._pendingRefreshKeys.has(conf.key)) {
          this.$logger.debug(`Skip refreshing ${conf.key}, pending write`)
          continue
        }
        if (!Object.is(this.$user[conf.key], conf.value)) {
          this.$logger.debug(`$refresh update: ${conf.key}`, conf.value)
          this.$patch((state) => {
            state.$user[conf.key] = conf.value
          })
        }
      }

      //app
      const a = useGeneratedConfigStore()
      a.$reset()
      let appConfig: ApplicationConfigEx[] = []
      counter = 0
      do {
        if (counter > 0) console.warn(`Failed to get application configs - retry #${counter}`)
        appConfig = (await apiConfigsGetCollection<false>({}).then((d) => d.data?.['hydra:member'])) as unknown as ApplicationConfigEx[]
        counter++
      } while (!appConfig && counter < 5)
      if (!appConfig || appConfig.length == 0) {
        console.warn('Received app config was empty!')
      }
      const appExcess: string[] = without<string>(Object.keys(this.$app), ...(appConfig ?? []).map<string>((e) => e.key as string))
      for (const key of appExcess) {
        this.$patch((state) => {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete state.$app[key]
        })
      }
      for (const conf of appConfig ?? []) {
        if (!conf.key) continue
        // this.$app[conf.key] = typeof conf.value === 'string' && (conf.value.startsWith('{') || conf.value.startsWith('[')) ? JSON.parse(conf.value) : conf.value
        if (this.$app[conf.key] != conf.value) {
          this.$patch((state) => {
            state.$app[conf.key] = conf.value
          })
        }
        // switch (typeof conf.value) {
        //     case 'boolean':
        //     case 'bigint':
        //     case 'number':
        //         this.$app[conf.key] = conf.value
        //         break
        //     case 'string':
        //         this.$app[conf.key] = conf.value.startsWith('{') || conf.value.startsWith('[') ? JSON.parse(conf.value) : conf.value
        //         break
        //     default:
        //         debugger
        //         break
        // }
      }

      this.$patch((state) => {
        state.$doUpdateOnChange = true
        state.ready = true
      })
    },
    // /**
    //  * Gets a ref to user-specific setting
    //  * @param key
    //  * @param defaultValue default value to set/return if not existing
    //  */
    getPathed<T>(key: string, defaultValue: T): PathedRef<T> {
      const router = useRouter()
      const isReal = ref(false)
      const wrapped = ref(unref(defaultValue))

      const resolve = async () => {
        await router.isReady()
        await until(() => router.currentRoute.value.path && this.ready).toBeTruthy()
        const realRef = this.get<T>(`${key}${router.currentRoute.value.path}`, defaultValue)
        wrapped.value = realRef.value
        watch(wrapped, (newVal) => {
          if (realRef.value !== newVal) {
            realRef.value = newVal
          }
        })

        watch(realRef, (newVal) => {
          if (wrapped.value !== newVal) {
            wrapped.value = newVal
          }
        })

        isReal.value = true
      }

      void resolve()

      // wir hängen einfach `isReal` daran dran
      return Object.assign(wrapped, {
        isReal: readonly(isReal)
      }) as PathedRef<T>
    },

    get<T>(key: string, defaultValue?: T): RemovableRef<T> {
      const baseRef = this.getRef<T>(key, defaultValue)

      return computed<T>({
        get: () => baseRef.value,
        set: (val) => (baseRef.value = val)
      })
    },

    getRef<T>(key: string, defaultValue?: T): RemovableRef<T> {
      // Wenn bereits gecached: sofort zurückgeben
      if (this._refCache.has(key)) {
        return this._refCache.get(key) as RemovableRef<T>
      }

      // Initial setzen, falls Wert fehlt
      if (!(key in this.$user) && defaultValue !== undefined) {
        this.set(key, defaultValue)
      }

      const getStoreValue = (): T | undefined => {
        const value = this.$user[key]
        return (value === undefined ? defaultValue : value) as T | undefined
      }

      // Ref mit Fallback-Handling bei späterem "undefiniert"
      const refValue = ref<T | undefined>(getStoreValue())

      // Store → Ref synchronisieren
      watch(
          () => this.$user[key],
          () => {
            refValue.value = getStoreValue()
          },
          { immediate: true }
      )

      // Ref → Store synchronisieren
      watch(refValue, (newVal) => {
        if (Object.is(toRaw(newVal), toRaw(getStoreValue()))) return

        this.set(key, newVal)
      })

      // Extended Ref mit remove()
      const extendedRef = Object.assign(refValue, {
        remove: () => {
          this._refCache.delete(key)
          this.set(key, null)
        }
      }) as RemovableRef<T>

      this._refCache.set(key, extendedRef)
      return extendedRef
    },
    /**
     * Gets a ref to application-wide setting
     * @param key
     * @param defaultValue
     */
    // appGet<T>(key: string, defaultVal: T | undefined = undefined): ComputedRef<T | undefined> {
    //     return computed(() => (this.$app[key] as T | undefined) ?? defaultVal)
    // },
    appGet<T>(key: string, defaultValue?: T): Readonly<Ref<T | undefined>> {
      const store = useConfigStore()
      const refValue = ref<T | undefined>((store.$app[key] as T | undefined) ?? defaultValue)
      watch(
          () => store.$app[key],
          (newValue) => {
            refValue.value = (newValue as T) ?? defaultValue
          }
      )
      return computed(() => refValue.value) as Readonly<Ref<T | undefined>>
    },
    /**
     * Sets user-specific setting
     * @param key
     * @param value value to set to or null to delete
     */

    set(key: string, value: unknown): void {
      void this.setAsync(key, value).catch((error) => {
        this.$logger.error(`Failed to save setting ${key}`, error)
      })
    },

    async setAsync(key: string, value: unknown): Promise<void> {
      this.$logger.info(`Changed setting ${key} to (${typeof value}) '${value?.toString()}'`)

      if (value === undefined) return
      if (!useTitrop().value.user.currentIri) {
        throw new Error('Tried to set employee config without having a current employee uri!')
      }

      this._pendingRefreshKeys.add(key)

      try {
        const store = useGeneratedEmployeeConfigStore()
        if (value === null) {
          const match = await store.find({ query: { key } })
          if (match.value?.id) {
            await store.delete({ path: { id: match.value.id.toString() } })
          }

          this.$patch((state) => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete state.$user[key]
          })
        } else {
          const data: EmployeeConfigDefaultWrite = {
            key,
            value: value as NonNullable<EmployeeConfigDefaultWrite['value']>,
            employee: useTitrop().value.user.currentIri
          }
          await store.post({ body: data })

          this.$patch((state) => {
            state.$user[key] = value
          })
        }
      } finally {
        this._pendingRefreshKeys.delete(key)
      }
    }
  }
})

useTitropEvents().onLoginSuccess.on(() => {
  const s = useConfigStore()
  void s.$refresh().then(() => {
    if (!s.$refresher) clearInterval(s.$refresher)
    s.$refresher = setInterval(() => void s.$refresh.bind(this), 30000)
  })
})

useTitropEvents().onLogout.on(() => {
  const s = useConfigStore()
  s.clear()
  if (!s.$refresher) clearInterval(s.$refresher)
  delete s.$refresher
})
